<template>
  <div v-if="state.visible" class="performance-alert-blocker">
    <div class="performance-alert-card">
      <div class="alert-header">
        <div class="alert-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div>
          <h4 class="alert-title mb-1">Alerta de rendimiento pendiente</h4>
          <p class="alert-subtitle mb-0">
            Para continuar en el sistema debes leer y firmar esta notificacion.
          </p>
        </div>
      </div>

      <div class="alert-content-wrapper">
        <div class="alert-content">
          <p v-if="alertView.message" class="alert-message">{{ alertView.message }}</p>
          <dl class="alert-facts">
            <div v-for="row in alertView.rows" :key="row.label" class="alert-fact">
              <dt>{{ row.label }}</dt>
              <dd>
                {{ row.value }}
                <span v-if="row.belowGoal" class="below-goal">Por debajo de la meta</span>
              </dd>
            </div>
          </dl>
          <div v-if="alertView.patients.length" class="patient-summary">
            <p class="patient-summary-title">Resumen de pacientes</p>
            <p v-for="(patient, index) in alertView.patients" :key="index" class="mb-1">{{ patient }}</p>
          </div>
        </div>
      </div>

      <div class="signature-title">
        <i class="fas fa-signature mr-2"></i>
        Firma del doctor (obligatoria)
      </div>

      <div class="signature-wrapper border border-primary rounded">
        <VueSignaturePad
          id="performance-alert-signature"
          ref="signaturePad"
          width="100%"
          height="200px"
          class="signature-pad"
          :options="{
            onBegin: () => {
              $refs.signaturePad.resizeCanvas()
            },
          }"
        />
      </div>

      <div class="mt-4 d-flex justify-content-end action-buttons">
        <b-button variant="outline-danger" class="mr-2" @click="clearSignature">
          <i class="far fa-trash-alt mr-50"></i>
          Limpiar firma
        </b-button>
        <b-button variant="primary" :disabled="state.signing" @click="submitSignature">
          <i class="fas fa-pen-nib mr-50"></i>
          <span v-if="!state.signing">Firmar y continuar</span>
          <span v-else>Firmando...</span>
        </b-button>
      </div>
    </div>
    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import ToastNotification from '@/components/ToastNotification'
import performanceAlertService from '@/core/services/performanceAlert.service'

export default {
  name: 'PerformanceAlertBlocker',
  components: {
    BButton,
    ToastNotification,
  },
  data() {
    return {
      state: performanceAlertService.state,
    }
  },
  computed: {
    alertView() {
      return performanceAlertService.buildAlertView(this.state.alertData)
    },
  },
  methods: {
    clearSignature() {
      this.$refs.signaturePad.clearSignature()
    },
    async submitSignature() {
      const { isEmpty, data } = this.$refs.signaturePad.saveSignature()
      if (isEmpty || !data) {
        this.$refs.toast.danger('La firma es obligatoria para continuar')
        return
      }

      try {
        await performanceAlertService.signPendingAlert(data)
        this.$refs.toast.success('Firma guardada correctamente')
        this.clearSignature()
      } catch (error) {
        this.$refs.toast.danger('No se pudo guardar la firma')
      }
    },
  },
}
</script>

<style scoped>
.performance-alert-blocker {
  position: fixed;
  inset: 0;
  z-index: 20000;
  background: linear-gradient(135deg, rgba(8, 15, 40, 0.82), rgba(28, 14, 45, 0.75));
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

.performance-alert-card {
  width: 100%;
  max-width: 960px;
  max-height: calc(100vh - 3rem);
  overflow: auto;
  background: #ffffff;
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 20px 50px rgba(15, 20, 40, 0.35);
}

.alert-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 1px solid #ffd3d3;
  background: linear-gradient(90deg, #fff5f5 0%, #fff 100%);
  border-radius: 10px;
  padding: 0.9rem 1rem;
  margin-bottom: 1rem;
}

.alert-icon {
  width: 46px;
  height: 46px;
  min-width: 46px;
  border-radius: 50%;
  background: #ffe1e1;
  color: #d63031;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.alert-title {
  color: #20233a;
  font-weight: 700;
}

.alert-subtitle {
  color: #555c7a;
  font-size: 0.95rem;
}

.alert-content-wrapper {
  border: 1px solid #e7e9f3;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.alert-content {
  background: #f9fafe;
  margin: 0;
  padding: 1rem 1.1rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #2d3150;
}

.alert-message {
  margin: 0 0 0.9rem;
  font-weight: 600;
  color: #20233a;
}

.alert-facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1.25rem;
  margin: 0;
}

.alert-fact {
  min-width: 0;
}

.alert-fact dt {
  margin: 0;
  color: #6b7190;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.alert-fact dd {
  margin: 0.15rem 0 0;
  color: #20233a;
  font-weight: 600;
}

.below-goal {
  display: inline-block;
  margin-left: 0.45rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: #ffe1e1;
  color: #d63031;
  font-size: 0.75rem;
  font-weight: 700;
  vertical-align: middle;
}

.patient-summary {
  margin-top: 1rem;
  padding-top: 0.85rem;
  border-top: 1px solid #e7e9f3;
}

.patient-summary-title {
  margin: 0 0 0.4rem;
  color: #6b7190;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.signature-title {
  font-weight: 600;
  color: #2f3560;
  margin-bottom: 0.65rem;
}

.signature-wrapper {
  background: #ffffff;
  border-radius: 10px;
  padding: 0.35rem;
}

.signature-pad {
  border-radius: 8px;
}

.action-buttons :deep(.btn) {
  min-width: 170px;
}

@media (max-width: 768px) {
  .performance-alert-card {
    padding: 1rem;
  }

  .alert-header {
    align-items: flex-start;
  }

  .action-buttons {
    flex-direction: column;
    gap: 0.6rem;
  }

  .action-buttons :deep(.btn) {
    width: 100%;
  }

  .alert-facts {
    grid-template-columns: 1fr;
  }
}
</style>
